import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signupService } from '../services/signupService';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Store token in state to persist it even if URL changes
  const [token, setToken] = useState(searchParams.get('token'));
  
  const [step, setStep] = useState('verifying'); // 'verifying', 'password-setup', 'success', 'error'
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Get token from URL if not already in state
    const urlToken = searchParams.get('token');
    if (urlToken && !token) {
      setToken(urlToken);
    }
    
    if (!token && !urlToken) {
      setError('Invalid verification link. Please check your email and try again.');
      setStep('error');
      return;
    }

    // Automatically verify email when component loads (only once)
    if (token || urlToken) {
      verifyEmail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyEmail = async () => {
    const currentToken = token || searchParams.get('token');
    if (!currentToken) return;

    try {
      setLoading(true);
      setError('');

      // Safe to run twice: the endpoint is idempotent, which matters because
      // this fires from a mount effect and React 18 StrictMode invokes those
      // twice in development.
      const data = await signupService.verifyEmail(currentToken);

      setEmail(data.email || '');
      setMessage(data.message || 'Email verified successfully');
      setStep('password-setup');
    } catch (err) {
      setError(err.message || 'Failed to verify email. Please try again.');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);

      const currentToken = token || searchParams.get('token');
      if (!currentToken) {
        throw new Error('Verification token is missing. Please use the verification link from your email.');
      }

      const data = await signupService.setPassword(currentToken, password);

      setStep('success');
      setMessage(data.message || 'Password set successfully');

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      console.error('Password setup error:', err);
      // Show more detailed error message
      if (err.message.includes('404') || err.message.includes('Not Found')) {
        // The API base is no longer interpolated here: it named a hardcoded
        // production hostname this file used to carry its own copy of, which is
        // both misleading now that the call goes through signupService and the
        // sort of thing that should not be printed on a public page.
        setError('We could not reach the sign-up service. Please try again shortly.');
      } else if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError(err.message || 'Failed to set password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (step === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white p-8 rounded-xl shadow-xl w-full max-w-md border border-gray-200 text-center">
          <div className="flex justify-center items-center mb-6">
            <img src="/sc_white.png" alt="SignalCue" className="h-[30px] w-auto" />
          </div>
          <div className="mb-4">
            <svg className="animate-spin h-12 w-12 text-[#00C6AE] mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying your email...</h2>
          <p className="text-gray-600 text-sm">Please wait while we verify your email address.</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white p-8 rounded-xl shadow-xl w-full max-w-md border border-gray-200">
          <div className="text-center mb-6">
            <div className="flex justify-center items-center mb-6">
              <img src="/sc_white.png" alt="SignalCue" className="h-[30px] w-auto" />
            </div>
            <div className="mb-4">
              <svg className="h-16 w-16 text-red-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verification Failed</h2>
            <p className="text-gray-600 text-sm mb-4">{error}</p>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-[#00C6AE] text-black py-3 rounded-lg font-semibold hover:bg-[#00C6AE]/90 transition-all"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white p-8 rounded-xl shadow-xl w-full max-w-md border border-gray-200 text-center">
          <div className="flex justify-center items-center mb-6">
            <img src="/sc_white.png" alt="SignalCue" className="h-[30px] w-auto" />
          </div>
          <div className="mb-4">
            <svg className="h-16 w-16 text-green-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Success!</h2>
          <p className="text-gray-600 text-sm mb-6">{message}</p>
          <p className="text-gray-500 text-xs">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Password setup form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white p-8 rounded-xl shadow-xl w-full max-w-md border border-gray-200">
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-6">
            <img src="/sc_white.png" alt="SignalCue" className="h-[30px] w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Set Your Password</h1>
          <p className="text-gray-600 text-sm">
            {email && `Email verified: ${email}`}
          </p>
          <p className="text-gray-600 text-sm mt-1">
            Please create a secure password to complete your account setup.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {message && !error && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {message}
          </div>
        )}

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-gray-700 text-sm font-medium mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-[#00C6AE] outline-none transition"
              placeholder="Enter your password (min. 8 characters)"
              required
              minLength={8}
              autoComplete="new-password"
            />
            <p className="text-xs text-gray-500 mt-1">Password must be at least 8 characters long</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-gray-700 text-sm font-medium mb-2">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-[#00C6AE] outline-none transition"
              placeholder="Confirm your password"
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#00C6AE] text-black py-3 rounded-lg font-semibold hover:bg-[#00C6AE]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Setting password...
              </span>
            ) : (
              'Set Password'
            )}
          </button>
        </form>

        <p className="text-center text-gray-500 text-xs mt-6">
          © 2026 SignalCue. All rights reserved.
        </p>
      </div>
    </div>
  );
}
